// src/App.js
import React, { useEffect, useMemo, useState, createContext, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box, Container, Typography } from "@mui/material";
import theme from "./theme";
import { api, clearAuth, getAuth, setAuth } from "./api";

import Header from "./components/header";
import FeedbackTab from "./components/FeedbackTab";
import Footer from "./components/footer";
import Home from "./components/home";
import MyCollection from "./components/mycollection";
import Wishlist from "./components/wishlist";
import Contact from "./components/Contact";
import Login from "./components/login";
import Manage from "./components/manage";
import Register from "./components/register";
import ProfitLoss from "./components/profitloss";
import ForgotPassword from "./components/forgotPassword";
import ResetPassword from "./components/resetPassword";
import SearchItems from "./components/search";
import ItemDetail from "./components/itemdetail";
import AddItem from "./components/additem";
import CollectionImportPage from "./components/collectionImport/CollectionImportPage";
import EditItem from "./components/EditItem";
import AccountsSearchPage from "./components/AccountsSearchPage";
import CommunityPage from "./components/CommunityPage";
import RoadToWorldCupPage from "./components/events/RoadToWorldCupPage";
import EB03ReleasePage from "./components/events/past-events/EB03ReleasePage";
import Pokemon30thAnniversaryPage from "./components/events/Pokemon30thAnniversaryPage";
import PerfectOrderPage from "./components/events/PerfectOrderPage";
import PokemonSetsPage from "./components/sets/PokemonSetsPage";
import PokemonSetRoutePage from "./components/sets/PokemonSetRoutePage";
import OnePieceSetsPage from "./components/sets/OnePieceSetsPage";
import OnePieceSetRoutePage from "./components/sets/OnePieceSetRoutePage";
import SportsSetsPage from "./components/sets/SportsSetsPage";
import SportsSetRoutePage from "./components/sets/SportsSetRoutePage";
import AllOtherSetsPage from "./components/sets/AllOtherSetsPage";
import AdminIntelligencePage from "./components/AdminIntelligencePage";
import AdminMobilePage from "./components/AdminMobilePage";
import { PriceVisibilityProvider } from "./contexts/PriceVisibilityContext";
import { CapabilitiesProvider, canAccessSiteOperations, useCapabilities } from "./contexts/CapabilitiesContext";

// Articles
import Article1 from "./components/article1";

// Legal
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";

const LS = window.localStorage;
const SS = window.sessionStorage;

// ===================== Auth Context =====================
const AuthContext = createContext({ user: null, ready: false });
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, ready: false });

  const syncAuthState = async () => {
    const { user } = getAuth();
    const safeUser = user || null;
    setState({ user: safeUser, ready: true });

    if (!safeUser?.id) return;

    let verifiedUser = safeUser;
    try {
      const { data } = await api.get("/api/me");
      verifiedUser = { ...safeUser, ...(data?.user || {}) };
    } catch {
      clearAuth();
      setState({ user: null, ready: true });
      return;
    }

    try {
      const { data } = await api.get(`/api/users/${verifiedUser.id || verifiedUser.user_id}`);
      const freshUser = data?.user || data || {};
      const mergedUser = { ...verifiedUser, ...freshUser };
      if (JSON.stringify(mergedUser) === JSON.stringify(safeUser)) return;

      const remember = Boolean(LS.getItem("user")) && !Boolean(SS.getItem("user"));
      setAuth({ user: mergedUser, remember });
      setState({ user: mergedUser, ready: true });
    } catch {
      setState({ user: safeUser, ready: true });
    }
  };

  // Rehydrate once on boot and refresh profile if authenticated.
  useEffect(() => {
    syncAuthState();
  }, []);

  // React to login/logout updates from this tab or other tabs.
  useEffect(() => {
    const onStorage = () => {
      syncAuthState();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => state, [state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Guard that waits for auth to load (prevents flicker to /login)
function PrivateRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return null; // or a spinner
  if (!user?.id) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function OperationsRoute({ children }) {
  const { user, ready } = useAuth();
  const { capabilities, capabilitiesLoaded } = useCapabilities();
  if (!ready || !capabilitiesLoaded) return null;
  if (!user?.id || !canAccessSiteOperations(capabilities)) return <Navigate to="/" replace />;
  return children;
}

function isAdminUser(user) {
  return Number(user?.is_admin || 0) === 1 || String(user?.role || "").toLowerCase() === "admin";
}

function AdminRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [verifiedUser, setVerifiedUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function verifyAdmin() {
      if (!ready || !user?.id) {
        setChecking(false);
        setVerifiedUser(null);
        return;
      }
      setChecking(true);
      try {
        const { data } = await api.get("/api/me");
        const nextUser = { ...user, ...(data?.user || {}) };
        if (!cancelled) {
          setVerifiedUser(nextUser);
        }
      } catch {
        if (!cancelled) {
          setVerifiedUser(null);
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    }
    verifyAdmin();
    return () => {
      cancelled = true;
    };
  }, [ready, user]);

  if (!ready || checking) return null;
  if (!user?.id) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isAdminUser(verifiedUser)) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            bgcolor: "background.paper",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            You do not have permission to view this page.
          </Typography>
        </Box>
      </Container>
    );
  }
  return children;
}

// Wrapper in case you add tabs later
function MyCollectionWrapper() {
  return <MyCollection />;
}

function WishlistWrapper() {
  return <Wishlist />;
}

function getWishlistRouteName(user) {
  return user?.username || user?.name || user?.id || "";
}

function WishlistRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user?.id) return <Navigate to="/login" replace />;
  const routeName = getWishlistRouteName(user);
  if (!routeName) return <WishlistWrapper />;
  return <Navigate to={`/wishlist/${encodeURIComponent(routeName)}`} replace />;
}
function PriceVisibilityBridge({ children }) {
  const { user, ready } = useAuth();
  return <PriceVisibilityProvider user={user} authReady={ready}>{children}</PriceVisibilityProvider>;
}

function CapabilitiesBridge({ children }) {
  const { user, ready } = useAuth();
  return <CapabilitiesProvider user={user} authReady={ready}>{children}</CapabilitiesProvider>;
}

// ===================== App Routes =====================
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />

      {/* Redirect base /mycollection to the logged-in user's collection, else to /login */}
      <Route
        path="/mycollection"
        element={<MyCollection />}
      />
      <Route path="/mycollection/import" element={<AdminRoute><CollectionImportPage /></AdminRoute>} />
      <Route path="/mycollection/import/:sessionId" element={<AdminRoute><CollectionImportPage /></AdminRoute>} />

      {/* Protected collection routes */}
      <Route
        path="/mycollection/:username"
        element={
          <PrivateRoute>
            <MyCollectionWrapper />
          </PrivateRoute>
        }
      />
      <Route
        path="/mycollection/:username/subcategory/:subId"
        element={
          <PrivateRoute>
            <MyCollectionWrapper />
          </PrivateRoute>
        }
      />

      {/* Public routes */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Login />} />
      <Route path="/manage" element={<Manage />} />
      <Route path="/register" element={<Register />} />
      <Route path="/accounts" element={<AccountsSearchPage />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/search" element={<SearchItems />} />
      <Route path="/item/:id" element={<ItemDetail />} />
      <Route path="/item/:id/:slug" element={<ItemDetail />} />
      <Route path="/wishlist" element={<WishlistRedirect />} />
      <Route
        path="/wishlist/:username"
        element={
          <PrivateRoute>
            <WishlistWrapper />
          </PrivateRoute>
        }
      />
      <Route path="/events/road-to-world-cup" element={<RoadToWorldCupPage />} />
      <Route path="/events/eb03-release" element={<EB03ReleasePage />} />
      <Route path="/events/op15-eb04" element={<Navigate to="/one-piece/sets/op15" replace />} />
      <Route path="/events/pokemon-30th-anniversary" element={<Pokemon30thAnniversaryPage />} />
      <Route path="/events/perfect-order" element={<PerfectOrderPage />} />
      <Route path="/sets/pokemon" element={<PokemonSetsPage />} />
      <Route path="/pokemon/sets/:setSlug" element={<PokemonSetRoutePage />} />
      <Route path="/sets/one-piece" element={<OnePieceSetsPage />} />
      <Route path="/one-piece/sets/eb04" element={<Navigate to="/one-piece/sets/op15" replace />} />
      <Route path="/one-piece/sets/op15-eb04" element={<Navigate to="/one-piece/sets/op15" replace />} />
      <Route path="/one-piece/sets/:setSlug" element={<OnePieceSetRoutePage />} />
      <Route path="/sets/football" element={<SportsSetsPage sport="football" />} />
      <Route path="/sets/hockey" element={<SportsSetsPage sport="hockey" />} />
      <Route path="/sets/basketball" element={<SportsSetsPage sport="basketball" />} />
      <Route path="/sets/soccer" element={<SportsSetsPage sport="soccer" />} />
      <Route path="/sports/football/sets/:setSlug" element={<SportsSetRoutePage sport="football" />} />
      <Route path="/sports/hockey/sets/:setSlug" element={<SportsSetRoutePage sport="hockey" />} />
      <Route path="/sports/basketball/sets/:setSlug" element={<SportsSetRoutePage sport="basketball" />} />
      <Route path="/sports/soccer/sets/:setSlug" element={<SportsSetRoutePage sport="soccer" />} />
      <Route path="/sets/all" element={<AllOtherSetsPage />} />
      <Route path="/add" element={<AddItem />} />
      <Route path="/edit" element={<EditItem />} />
      <Route
        path="/admin/intelligence"
        element={
          <AdminRoute>
            <AdminIntelligencePage />
          </AdminRoute>
        }
      />
      <Route path="/admin/mobile" element={<OperationsRoute><AdminMobilePage /></OperationsRoute>} />

      {/* Profit/Loss routes (new) */}
      <Route
        path="/profitloss"
        element={<ProfitLoss />}
      />
      <Route
        path="/profitloss/:username"
        element={
          <PrivateRoute>
            <ProfitLoss />
          </PrivateRoute>
        }
      />
      <Route
        path="/manage/:username"
        element={
          <PrivateRoute>
            <Manage />
          </PrivateRoute>
        }
      />
      {/* Legacy support: /profit-loss -> /profitloss */}
      <Route path="/profit-loss" element={<Navigate to="/profitloss" replace />} />

      {/* Articles */}
      <Route path="/article1" element={<Article1 />} />

      {/* Legal */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search, hash]);
  return null;
}

// ===================== Main App =====================
export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CapabilitiesBridge>
          <PriceVisibilityBridge>
          <Router>
          <ScrollToTop />
          <Box
            className="app-container"
            sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}
          >
            <Header onLogout={clearAuth} />
            <Box
              component="main"
              className="main-content"
              sx={{ flexGrow: 1, pt: "var(--tcs-header-offset, 120px)", bgcolor: "background.default" }}
            >
              <Container
                maxWidth="xl"
                sx={{
                  px: { xs: 2, sm: 2.5, md: 3 },
                  py: { xs: 2, sm: 2.5, md: 3 },
                }}
              >
                <AppRoutes />
              </Container>
            </Box>
            <FeedbackTab />
            <Footer />
          </Box>
          </Router>
          </PriceVisibilityBridge>
        </CapabilitiesBridge>
      </AuthProvider>
    </ThemeProvider>
  );
}
